import { Capacitor } from '@capacitor/core';

export type QrScanCancellation = 'user_cancelled';

export type QrScanErrorReason =
    | 'permission_denied'
    | 'no_camera'
    | 'unsupported'
    | 'unknown';

export class QrScanError extends Error {
    public readonly reason: QrScanErrorReason;

    constructor(reason: QrScanErrorReason, message: string) {
        super(message);
        this.reason = reason;
        this.name = 'QrScanError';
    }
}

export interface QrScanResult {
    value: string;
}

interface NativeBarcodePermissionResult {
    camera: 'granted' | 'denied' | 'prompt' | 'prompt-with-rationale' | 'limited' | string;
}

interface NativeBarcode {
    rawValue?: string;
    displayValue?: string;
}

interface NativeScanResult {
    barcodes: NativeBarcode[];
}

interface NativeBarcodeScannerModule {
    BarcodeScanner: {
        checkPermissions(): Promise<NativeBarcodePermissionResult>;
        requestPermissions(): Promise<NativeBarcodePermissionResult>;
        scan(options: { formats: string[] }): Promise<NativeScanResult>;
        isSupported?(): Promise<{ supported: boolean }>;
    };
    BarcodeFormat: { QrCode: string };
}

async function ensureNativePermission(module: NativeBarcodeScannerModule): Promise<void> {
    const current = await module.BarcodeScanner.checkPermissions();

    if (current.camera === 'granted' || current.camera === 'limited') {
        return;
    }

    if (current.camera === 'denied') {
        throw new QrScanError(
            'permission_denied',
            'La aplicación necesita permiso de cámara para escanear el código QR.',
        );
    }

    const requested = await module.BarcodeScanner.requestPermissions();

    if (requested.camera !== 'granted' && requested.camera !== 'limited') {
        throw new QrScanError(
            'permission_denied',
            'La aplicación necesita permiso de cámara para escanear el código QR.',
        );
    }
}

async function scanWithCapacitor(): Promise<QrScanResult | null> {
    let module: NativeBarcodeScannerModule;

    try {
        module = (await import('@capacitor-mlkit/barcode-scanning')) as unknown as NativeBarcodeScannerModule;
    } catch {
        throw new QrScanError('unsupported', 'El plugin de escáner no está disponible en esta versión.');
    }

    if (typeof module.BarcodeScanner.isSupported === 'function') {
        const supported = await module.BarcodeScanner.isSupported();

        if (!supported.supported) {
            throw new QrScanError('no_camera', 'Este dispositivo no soporta el escaneo de códigos QR.');
        }
    }

    await ensureNativePermission(module);

    const result = await module.BarcodeScanner.scan({
        formats: [module.BarcodeFormat?.QrCode ?? 'QR_CODE'],
    });

    const value = result.barcodes?.[0]?.rawValue ?? result.barcodes?.[0]?.displayValue ?? null;

    if (!value) {
        return null;
    }

    return { value };
}

interface WebQrScannerOptions {
    onCancel?: () => void;
}

interface WebQrScannerInstance {
    start(): Promise<void>;
    stop(): void;
    destroy(): void;
}

interface WebQrScannerConstructor {
    new (
        videoElement: HTMLVideoElement,
        onDecode: (result: { data: string }) => void,
        options: { highlightScanRegion?: boolean; preferredCamera?: 'environment' | 'user' },
    ): WebQrScannerInstance;
    hasCamera(): Promise<boolean>;
}

async function scanWithWeb(options: WebQrScannerOptions = {}): Promise<QrScanResult | null> {
    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
        throw new QrScanError('unsupported', 'Tu navegador no soporta la cámara.');
    }

    let module: { default: WebQrScannerConstructor };

    try {
        module = (await import('qr-scanner')) as unknown as { default: WebQrScannerConstructor };
    } catch {
        throw new QrScanError('unsupported', 'No se pudo cargar el escáner.');
    }

    const QrScannerCtor = module.default;
    const hasCamera = await QrScannerCtor.hasCamera();

    if (!hasCamera) {
        throw new QrScanError('no_camera', 'No se detectó una cámara disponible.');
    }

    return new Promise<QrScanResult | null>((resolve, reject) => {
        const overlay = document.createElement('div');
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-label', 'Escanear código QR');
        overlay.style.cssText = [
            'position:fixed',
            'inset:0',
            'background:rgba(15,15,15,0.92)',
            'z-index:9999',
            'display:flex',
            'flex-direction:column',
            'align-items:center',
            'justify-content:center',
            'padding:1rem',
        ].join(';');

        const video = document.createElement('video');
        video.setAttribute('playsinline', 'true');
        video.style.cssText = [
            'width:100%',
            'max-width:24rem',
            'aspect-ratio:1/1',
            'object-fit:cover',
            'border-radius:1rem',
            'background:#000',
        ].join(';');

        const hint = document.createElement('p');
        hint.textContent = 'Apunta al código QR del cliente.';
        hint.style.cssText = 'color:#fff;margin-top:1rem;font-family:system-ui;font-size:0.95rem;text-align:center;';

        const cancelButton = document.createElement('button');
        cancelButton.type = 'button';
        cancelButton.textContent = 'Cancelar';
        cancelButton.style.cssText = [
            'margin-top:1.25rem',
            'padding:0.6rem 1.4rem',
            'border-radius:9999px',
            'border:1px solid rgba(255,255,255,0.4)',
            'background:transparent',
            'color:#fff',
            'font-family:system-ui',
            'font-size:0.95rem',
            'cursor:pointer',
        ].join(';');

        overlay.append(video, hint, cancelButton);
        document.body.appendChild(overlay);

        let scanner: WebQrScannerInstance | null = null;
        let settled = false;

        const cleanup = () => {
            if (scanner) {
                try {
                    scanner.stop();
                    scanner.destroy();
                } catch {
                    // ignore cleanup errors
                }
                scanner = null;
            }

            if (overlay.parentElement) {
                overlay.parentElement.removeChild(overlay);
            }
        };

        const finish = (result: QrScanResult | null) => {
            if (settled) {
                return;
            }
            settled = true;
            cleanup();
            resolve(result);
        };

        const fail = (errorReason: QrScanErrorReason, message: string) => {
            if (settled) {
                return;
            }
            settled = true;
            cleanup();
            reject(new QrScanError(errorReason, message));
        };

        cancelButton.addEventListener('click', () => {
            options.onCancel?.();
            finish(null);
        });

        try {
            scanner = new QrScannerCtor(
                video,
                (decoded) => {
                    if (decoded?.data) {
                        finish({ value: decoded.data });
                    }
                },
                { highlightScanRegion: true, preferredCamera: 'environment' },
            );
        } catch {
            fail('unknown', 'No se pudo iniciar el escáner.');
            return;
        }

        scanner.start().catch((startError: unknown) => {
            const name = (startError as { name?: string })?.name ?? '';
            if (name === 'NotAllowedError' || name === 'SecurityError') {
                fail('permission_denied', 'Concede permiso de cámara para escanear el QR.');
                return;
            }

            if (name === 'NotFoundError' || name === 'OverconstrainedError') {
                fail('no_camera', 'No se pudo acceder a la cámara.');
                return;
            }

            fail('unknown', 'No se pudo iniciar la cámara.');
        });
    });
}

export async function scanQrOnce(options: WebQrScannerOptions = {}): Promise<QrScanResult | null> {
    if (Capacitor.isNativePlatform()) {
        return scanWithCapacitor();
    }

    return scanWithWeb(options);
}
